import { SxProps, Theme } from "@mui/material";
import checkoutTokens from "./Checkout.tokens";

const t = checkoutTokens;

/** Vertical stack holding the back link and the two-column grid. */
export const checkoutViewSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.section,
};

/** "← Back to plans". */
export const backLinkSx: SxProps<Theme> = {
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  gap: t.space.backLinkGap,
  p: 0,
  minWidth: 0,
  // <button> does not inherit the page font on its own.
  fontFamily: "inherit",
  fontSize: t.font.backLink,
  fontWeight: t.weight.strong,
  lineHeight: t.lineHeight.tight,
  color: t.color.body,
  textTransform: "none",
  "&:hover": { color: t.color.ink, bgcolor: "transparent" },
  "&:focus-visible": {
    outline: `2px solid ${t.color.focusRing}`,
    outlineOffset: "2px",
    borderRadius: t.radius.changeHit,
  },
};

/** Order summary (fluid) beside the payment card (fixed 420px). */
export const checkoutGridSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0,1fr)",
    md: `minmax(0,1fr) ${t.size.paymentColumn}`,
  },
  gap: t.space.columnGap,
  alignItems: "start",
};

/** White rounded card chrome shared by both columns. */
export const cardSx = (gap: string): SxProps<Theme> => ({
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${t.color.cardBorder}`,
  borderRadius: t.radius.card,
  boxShadow: t.shadow.card,
  px: t.space.cardPaddingX,
  py: t.space.cardPaddingY,
  display: "flex",
  flexDirection: "column",
  gap,
  minWidth: 0,
  boxSizing: "border-box",
});

export const cardHeadSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.headGap,
};

export const cardTitleSx: SxProps<Theme> = {
  // Rendered as a heading element; reset the browser's default heading margin.
  m: 0,
  fontSize: t.font.cardTitle,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: t.lineHeight.tight,
};

export const cardSubSx: SxProps<Theme> = {
  fontSize: t.font.cardSub,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: t.lineHeight.tight,
};

/* ------------------------------- order summary ------------------------------ */

export const summaryRowsSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
};

/** One priced line; the last one drops its rule and grows into the total. */
export const summaryRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.space.rowGap,
  py: t.space.rowY,
  borderBottom: `${t.border.hairline} solid ${t.color.rowDivider}`,
};

/** Row label — `emphasised` is the plan line, the rest are descriptions. */
export const summaryLabelSx = (emphasised: boolean): SxProps<Theme> => ({
  fontSize: t.font.rowLabel,
  fontWeight: emphasised ? t.weight.medium : t.weight.regular,
  color: emphasised ? t.color.ink : t.color.body,
  lineHeight: t.lineHeight.tight,
});

export const summaryValueSx: SxProps<Theme> = {
  ml: "auto",
  fontSize: t.font.rowValue,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
  whiteSpace: "nowrap",
};

/** Trailing "/ month" inside the plan line's amount. */
export const summaryUnitSx: SxProps<Theme> = {
  fontSize: t.font.rowUnit,
  fontWeight: t.weight.medium,
  color: t.color.muted,
};

/** Green "billed once per year" callout under the plan line. */
export const annualNoteSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.space.annualNoteGap,
  px: t.space.annualNoteX,
  py: t.space.annualNoteY,
  mt: t.space.annualNoteTop,
  bgcolor: t.color.successBg,
  borderRadius: t.radius.annualNote,
};

export const annualNoteTextSx: SxProps<Theme> = {
  fontSize: t.font.annualNote,
  fontWeight: t.weight.medium,
  color: t.color.success,
  lineHeight: t.lineHeight.tight,
};

export const totalRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.space.rowGap,
  pt: t.space.totalTop,
  pb: t.space.totalBottom,
};

export const totalLabelSx: SxProps<Theme> = {
  fontSize: t.font.totalLabel,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
};

export const totalValueSx: SxProps<Theme> = {
  ml: "auto",
  fontSize: t.font.totalValue,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: t.lineHeight.tight,
};

export const renewNoteSx: SxProps<Theme> = {
  fontSize: t.font.renewNote,
  fontWeight: t.weight.regular,
  color: t.color.muted,
  lineHeight: t.lineHeight.renewNote,
};

/* --------------------------------- payment --------------------------------- */

export const paymentHeadSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.space.optionGap,
};

export const poweredBySx: SxProps<Theme> = {
  ml: "auto",
  fontSize: t.font.poweredBy,
  fontWeight: t.weight.medium,
  color: t.color.muted,
  lineHeight: t.lineHeight.tight,
};

export const stripeWordmarkSx: SxProps<Theme> = {
  fontWeight: t.weight.heading,
  color: t.color.stripeBrand,
};

/** Outer chrome of a payment option (saved card / new card). */
export const optionShellSx = (selected: boolean): SxProps<Theme> => ({
  position: "relative",
  border: `${t.border.emphasis} solid ${
    selected ? t.color.accent : t.color.cardBorder
  }`,
  bgcolor: selected ? t.color.selectedBg : t.color.surface,
  borderRadius: t.radius.option,
  boxSizing: "border-box",
});

/** Clickable row inside an option. */
export const optionRowSx: SxProps<Theme> = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: t.space.optionGap,
  px: t.space.optionX,
  py: t.space.optionY,
  borderRadius: t.radius.option,
  textAlign: "left",
  fontFamily: "inherit",
  boxSizing: "border-box",
  cursor: "pointer",
};

export const optionTargetSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: t.space.optionGap,
  p: 0,
  borderRadius: t.radius.changeHit,
  textAlign: "left",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

/** "Use a new card" — shell and row collapse onto a single control. */
export const newCardOptionSx = (selected: boolean): SxProps<Theme> => ({
  position: "relative",
  border: `${t.border.emphasis} solid ${
    selected ? t.color.accent : t.color.cardBorder
  }`,
  bgcolor: selected ? t.color.selectedBg : t.color.surface,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: t.space.optionGap,
  px: t.space.optionX,
  py: t.space.optionY,
  borderRadius: t.radius.option,
  textAlign: "left",
  fontFamily: "inherit",
  boxSizing: "border-box",
});

export const radioSx = (selected: boolean): SxProps<Theme> => ({
  width: t.size.radio,
  height: t.size.radio,
  flexShrink: 0,
  borderRadius: t.radius.pill,
  border: `${t.border.emphasis} solid ${
    selected ? t.color.accent : t.color.radioIdle
  }`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
});

export const radioDotSx = (selected: boolean): SxProps<Theme> => ({
  width: t.size.radioDot,
  height: t.size.radioDot,
  borderRadius: t.radius.pill,
  bgcolor: selected ? t.color.ctaBg : "transparent",
});

/** Card-brand plate ("DISC", "UNION", "MC"). */
export const brandPlateSx = (color: string): SxProps<Theme> => ({
  width: t.size.plateWidth,
  height: t.size.plateHeight,
  flexShrink: 0,
  borderRadius: t.radius.plate,
  bgcolor: t.color.plateBg,
  border: `${t.border.hairline} solid ${t.color.plateBorder}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: t.font.brandPlate,
  fontWeight: t.weight.heading,
  lineHeight: t.lineHeight.tight,
  color,
});

/** Card-network logo, occupying the same box as the text plate. */
export const cardLogoSx: SxProps<Theme> = {
  width: t.size.cardLogoWidth,
  height: t.size.cardLogoHeight,
  flexShrink: 0,
  display: "block",
  objectFit: "contain",
};

export const cardLineSx: SxProps<Theme> = {
  fontSize: t.font.cardLine,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
  whiteSpace: "nowrap",
};

export const primaryPillSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: t.font.primaryPill,
  fontWeight: t.weight.strong,
  lineHeight: t.lineHeight.tight,
  color: t.color.success,
  bgcolor: t.color.successBg,
  px: t.space.primaryPillX,
  py: t.space.primaryPillY,
  borderRadius: t.radius.pill,
};

/** "Change ⌄" toggle for the saved-card picker. */
export const changeLinkSx: SxProps<Theme> = {
  ml: "auto",
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: t.space.changeGap,
  px: t.space.changeHitX,
  py: t.space.changeHitY,
  minWidth: 0,
  borderRadius: t.radius.changeHit,
  fontFamily: "inherit",
  fontSize: t.font.changeLink,
  fontWeight: t.weight.strong,
  color: t.color.accent,
  lineHeight: t.lineHeight.tight,
  textTransform: "none",
  "&:hover": { bgcolor: t.color.changeHoverBg },
};

export const chevronSx = (open: boolean): SxProps<Theme> => ({
  transform: open ? "rotate(180deg)" : "rotate(0deg)",
  transition: "transform .15s",
});

/** Saved-card dropdown, overhanging the option's 1.5px border. */
export const pickerSx: SxProps<Theme> = {
  position: "absolute",
  left: t.size.pickerOverhang,
  right: t.size.pickerOverhang,
  top: `calc(100% + ${t.size.pickerOffset})`,
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${t.color.cardBorder}`,
  borderRadius: t.radius.picker,
  boxShadow: t.shadow.picker,
  zIndex: t.size.pickerZIndex,
  maxHeight: t.size.pickerMaxHeight,
  overflowY: "auto",
};

export const pickerRowSx = (selected: boolean): SxProps<Theme> => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: t.space.optionGap,
  px: t.space.pickerRowX,
  py: t.space.pickerRowY,
  bgcolor: selected ? t.color.selectedBg : t.color.surface,
  borderBottom: `${t.border.hairline} solid ${t.color.rowDivider}`,
  borderRadius: 0,
  textAlign: "left",
  fontFamily: "inherit",
  boxSizing: "border-box",
  "&:hover": {
    bgcolor: selected ? t.color.selectedBg : t.color.pickerHoverBg,
  },
});

export const optionLabelSx: SxProps<Theme> = {
  fontSize: t.font.optionLabel,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
};

/* ------------------------------- new-card form ------------------------------ */

export const fieldGroupSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.fieldGap,
};

export const fieldLabelSx: SxProps<Theme> = {
  fontSize: t.font.fieldLabel,
  fontWeight: t.weight.medium,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
};

/** Combined card-information box: number row above expiry | cvc. */
export const cardBoxSx: SxProps<Theme> = {
  border: `${t.border.hairline} solid ${t.color.controlBorder}`,
  borderRadius: t.radius.control,
  overflow: "hidden",
  bgcolor: t.color.surface,
};

export const cardNumberRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.space.cardFieldGap,
  px: t.space.fieldPaddingX,
  height: t.size.fieldHeight,
  borderBottom: `${t.border.hairline} solid ${t.color.controlBorder}`,
  boxSizing: "border-box",
};

/** Expiry | CVC pair under the number row. */
export const cardSplitRowSx: SxProps<Theme> = {
  display: "flex",
};

export const cardSplitCellSx = (divided: boolean): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  height: t.size.fieldHeight,
  display: "flex",
  alignItems: "center",
  px: t.space.fieldPaddingX,
  borderRight: divided
    ? `${t.border.hairline} solid ${t.color.controlBorder}`
    : "none",
  boxSizing: "border-box",
});

/** Wrapper that lets a Stripe element iframe fill its cell. */
export const stripeFieldSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  "& .StripeElement": { width: "100%" },
};

export const networkChipRowSx: SxProps<Theme> = {
  display: "flex",
  gap: t.space.networkChipGap,
  flexShrink: 0,
};

export const networkChipSx = (color: string): SxProps<Theme> => ({
  width: t.size.networkChipWidth,
  height: t.size.networkChipHeight,
  borderRadius: t.radius.networkChip,
  bgcolor: t.color.plateBg,
  border: `${t.border.hairline} solid ${t.color.plateBorder}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: t.font.networkChip,
  fontWeight: t.weight.heading,
  lineHeight: t.lineHeight.tight,
  color,
});

/** Plain text input matching the design's 40px fields. */
export const textFieldSx: SxProps<Theme> = {
  height: t.size.fieldHeight,
  border: `${t.border.hairline} solid ${t.color.controlBorder}`,
  bgcolor: t.color.surface,
  borderRadius: t.radius.control,
  px: t.space.fieldPaddingX,
  boxSizing: "border-box",
  "& .MuiInputBase-root": { height: "100%" },
  "& .MuiInputBase-input": {
    p: 0,
    height: "100%",
    fontFamily: "inherit",
    fontSize: t.font.fieldText,
    fontWeight: t.weight.field,
    color: t.color.ink,
    lineHeight: t.lineHeight.tight,
    // The theme restyles every InputBase placeholder (14px / 400); the design
    // keeps the field's own type, so restate it here.
    "&::placeholder": {
      fontSize: t.font.fieldText,
      fontWeight: t.weight.field,
      color: t.color.placeholder,
      opacity: 1,
    },
  },
};

/** Country / ZIP pair. */
export const fieldRowSx: SxProps<Theme> = {
  display: "flex",
  gap: t.space.fieldRowGap,
};

export const countryColumnSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: t.space.fieldGap,
};

export const zipColumnSx: SxProps<Theme> = {
  width: t.size.zipWidth,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  gap: t.space.fieldGap,
};

/**
 * Country select. Driven by an `InputBase` rather than the outlined variant so
 * the border is a real `border` on the control (like the design) instead of
 * MUI's overlaid `fieldset`, which sits 5px above the box.
 */
export const countrySelectSx: SxProps<Theme> = {
  height: t.size.fieldHeight,
  border: `${t.border.hairline} solid ${t.color.controlBorder}`,
  bgcolor: t.color.surface,
  borderRadius: t.radius.control,
  boxSizing: "border-box",
  // The select slot also carries `.MuiInputBase-input`, which the theme
  // restyles — type has to be restated on the slot, not just the root.
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    minHeight: "unset",
    boxSizing: "border-box",
    px: t.space.fieldPaddingX,
    py: 0,
    fontFamily: "inherit",
    fontSize: t.font.fieldText,
    fontWeight: t.weight.field,
    color: t.color.ink,
    lineHeight: t.lineHeight.tight,
  },
  "& .MuiSelect-icon": {
    right: t.space.fieldPaddingX,
    width: t.size.chevron,
    height: t.size.chevron,
    top: `calc(50% - ${t.size.chevron / 2}px)`,
    color: t.color.muted,
  },
};

export const countryMenuItemSx: SxProps<Theme> = {
  fontSize: t.font.fieldText,
  fontWeight: t.weight.field,
  color: t.color.ink,
};

/* --------------------------------- footer ---------------------------------- */

export const payButtonSx: SxProps<Theme> = {
  height: t.size.payButtonHeight,
  minHeight: t.size.payButtonHeight,
  mt: t.space.payButtonTop,
  px: 0,
  py: 0,
  bgcolor: t.color.ctaBg,
  color: t.color.onAccent,
  border: "none",
  borderRadius: t.radius.control,
  fontSize: t.font.payButton,
  fontWeight: t.weight.strong,
  // MUI's button typography adds tracking; the design's buttons have none.
  letterSpacing: "normal",
  lineHeight: t.lineHeight.tight,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: t.color.ctaBgHover, boxShadow: "none" },
  "&.Mui-disabled": {
    bgcolor: t.color.ctaBg,
    opacity: 0.5,
    color: t.color.onAccent,
  },
};

export const secureNoteSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: t.space.secureGap,
  fontSize: t.font.secureNote,
  fontWeight: t.weight.regular,
  color: t.color.muted,
  lineHeight: t.lineHeight.tight,
};
